import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { ChargeDetails } from './ChargeDetails';
import { BenefitList } from './BenefitList';
import { useAuth } from '@/hooks/useAuth';
import { ACCOUNT_BILLING_CREDITS_PATH } from '@/routes/paths';

const PREMIUM_COST = 55;

interface CollectInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  variant?: 'free' | 'premium';
}

/** Confirmation modal for free/premium internet data collection. */
export const CollectInfoModal: React.FC<CollectInfoModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  variant = 'free',
}) => {
  const [activated, setActivated] = React.useState(false);
  const isPremium = variant === 'premium';
  const { user } = useAuth();
  const navigate = useNavigate();

  const userCredits = user?.credits ?? 0;
  const hasEnoughCredits = !isPremium || userCredits >= PREMIUM_COST;

  const handleActivate = () => {
    setActivated(true);
    onConfirm();
  };

  React.useEffect(() => {
    if (!isOpen) setActivated(false);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className='fixed inset-0 z-modal flex items-center justify-center p-4'>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='absolute inset-0 bg-black/40 backdrop-blur-sm'
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={clsx(
              'relative flex w-full max-w-[650px] flex-col overflow-hidden',
              'max-h-[90vh]',
              'rounded-20 border-4 border-brand-800',
              'bg-white shadow-2xl'
            )}
            style={{
              background: `linear-gradient(180deg, #EEEAFF -102.61%, rgba(255, 255, 255, 0) 140.84%), #FFFFFF`,
            }}
          >
            <button
              onClick={onClose}
              className='absolute right-4 top-4 z-10 rounded-full bg-red-50 p-2 text-red-400 transition-colors hover:bg-red-100'
            >
              <XMarkIcon className='size-5' />
            </button>

            <div className='global-scrollbar flex flex-col gap-8 overflow-y-auto p-8 pt-14 md:p-10 md:pt-14'>
              <div className='flex flex-col gap-4'>
                <h2 className='font-sans text-32 font-bold leading-27 text-brand-800'>
                  {isPremium ? 'Premium Internet Search' : 'Collect Info from Internet'}
                </h2>
                <p className='font-sans text-16 font-normal leading-24 text-gray-600'>
                  {isPremium
                    ? 'Our premium search performs a deep analysis across multiple platforms, interviews, and public sources to create the most accurate and detailed personality for your echo.'
                    : "We'll search social media profiles, public content, and online sources to build a richer, more authentic personality for your echo. This search runs automatically when you start training."}
                </p>
              </div>

              <ChargeDetails isActivated={activated} variant={variant} />
              <BenefitList variant={variant} />

              {isPremium && !hasEnoughCredits && !activated && (
                <div className='rounded-10 border border-amber-200 bg-amber-50 p-4'>
                  <p className='font-sans text-14 font-medium text-amber-800'>
                    You need {PREMIUM_COST} credits but only have {userCredits}. Purchase more
                    credits to activate premium search.
                  </p>
                </div>
              )}

              <div className='flex justify-center'>
                {isPremium && !hasEnoughCredits && !activated ? (
                  <Button
                    variant='settings-modify'
                    onClick={() => navigate(ACCOUNT_BILLING_CREDITS_PATH)}
                    className='h-[48px] w-glow'
                  >
                    Buy Credits
                  </Button>
                ) : (
                  <Button
                    variant='settings-modify'
                    onClick={handleActivate}
                    disabled={isLoading || activated}
                    className='h-[48px] w-glow'
                  >
                    {activated ? 'Activated' : isLoading ? 'Processing...' : 'Activate'}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
